import { type RefObject, useEffect, useRef, useState } from "react";

// Reports whether the observed element is within `rootMargin` of the viewport.
// Once far, it waits `teardownDelayMs` before flipping back to false, so a fast
// scroll past an element doesn't thrash whatever heavy subtree it gates.
// Pass `once: true` to latch on the first intersection and never flip back, for
// subtrees that are expensive to rebuild once shown.
export function useNearViewport(
  ref: RefObject<Element | null>,
  {
    rootMargin = "400px",
    teardownDelayMs = 750,
    once = false,
  }: { rootMargin?: string; teardownDelayMs?: number; once?: boolean } = {}
): boolean {
  const [near, setNear] = useState(false);
  const nearRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const set = (value: boolean) => {
      nearRef.current = value;
      setNear(value);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          clearTimer();
          set(true);
          if (once) obs.disconnect();
        } else if (!once && nearRef.current && timerRef.current === null) {
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            set(false);
          }, teardownDelayMs);
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimer();
    };
  }, [ref, rootMargin, teardownDelayMs, once]);

  return near;
}
